import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getStoredTenant, buildTenantPath, hasTenantInPath } from 'src/utils/tenantUtils';

/**
 * Auto-redirect component for tenant persistence.
 *
 * Checks if current URL has tenant. If not, checks localStorage for last-used tenant
 * and redirects to tenant-scoped version of the current path.
 *
 * Prevents redirect loops by tracking the last redirect time in sessionStorage.
 *
 * Usage: Wrap login/root routes that should support tenant auto-redirect
 *
 * @example
 * User visits /login (no tenant in URL)
 * → Checks localStorage → finds "rds-test"
 * → Redirects to /t/rds-test/login (ONE TIME)
 *
 * @example
 * User visits /t/rds-test/login (has tenant)
 * → No redirect, renders children normally
 */
interface TenantAutoRedirectProps {
  children: React.ReactNode;
}

const REDIRECT_COOLDOWN_MS = 3000; // Don't redirect again within 3 seconds
const LAST_REDIRECT_KEY = 'tenantAutoRedirectLastTime';

export default function TenantAutoRedirect({ children }: TenantAutoRedirectProps) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;

    // If URL already has tenant, don't redirect
    if (hasTenantInPath(currentPath)) {
      return;
    }

    // Prevent redirect loops - if we just redirected, don't redirect again
    const lastRedirectTimeStr = sessionStorage.getItem(LAST_REDIRECT_KEY);
    const now = Date.now();

    if (lastRedirectTimeStr) {
      const lastRedirectTime = parseInt(lastRedirectTimeStr, 10);
      const timeSinceLastRedirect = now - lastRedirectTime;

      if (timeSinceLastRedirect < REDIRECT_COOLDOWN_MS) {
        return;
      }
    }

    // Check for stored tenant in localStorage
    const storedTenant = getStoredTenant();
    if (storedTenant) {
      // Build tenant-scoped path and redirect
      const tenantPath = buildTenantPath(storedTenant, currentPath);

      // Store redirect timestamp to prevent loops
      sessionStorage.setItem(LAST_REDIRECT_KEY, now.toString());

      navigate(tenantPath, { replace: true });
    }
    // If no stored tenant, render normally (first-time visitor)
  }, [location.pathname, navigate]);

  return <>{children}</>;
}
